import { useEffect, useState } from "react";
import { useSearchParams, Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Copy, Share2, Gift } from "lucide-react";
import ReferralCelebrationModal from "@/components/ReferralCelebrationModal";

function ReferralShareMoment() {
  const [ref, setRef] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals/me").then(r => setRef(r.data)).catch(() => {});
  }, []);

  if (!ref) return null;
  const inviteUrl = `${window.location.origin}${ref.invite_url_suffix}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* ignore */ }
  };
  const share = async () => {
    const data = {
      title: "Join The Farm To You",
      text: `I just joined The Farm To You — get $${ref.reward_per_conversion} off your first month with my invite.`,
      url: inviteUrl,
    };
    if (navigator.share) {
      try { await navigator.share(data); } catch (e) { /* user-cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <div className="mt-12 card-flat p-6 text-left" data-testid="referral-share-card">
      <div className="flex items-center gap-2">
        <Gift size={18} className="text-[var(--ff-primary)]"/>
        <p className="overline" data-testid="referral-share-overline">Give $10, get $10</p>
      </div>
      <h2 className="font-display text-2xl mt-2" data-testid="referral-share-headline">
        Share The Farm To You with a friend
      </h2>
      <p className="text-sm text-[var(--ff-text-secondary)] mt-2 leading-relaxed">
        For every friend who joins with your link, you both get <span className="font-semibold text-[var(--ff-primary)]">${ref.reward_per_conversion} credit</span> toward your next renewal.
      </p>

      <div className="mt-4 flex items-stretch gap-2 flex-col sm:flex-row">
        <div className="input-base flex-1 truncate text-sm select-all" data-testid="referral-invite-url">
          {inviteUrl}
        </div>
        <button onClick={copy} className="btn-secondary inline-flex items-center gap-2" data-testid="referral-copy-btn">
          <Copy size={16}/> {copied ? "Copied!" : "Copy link"}
        </button>
        <button onClick={share} className="btn-primary inline-flex items-center gap-2" data-testid="referral-share-btn">
          <Share2 size={16}/> Share
        </button>
      </div>

      <p className="mt-3 text-xs text-[var(--ff-text-muted)]" data-testid="referral-code-line">
        Your code: <span className="font-mono font-semibold tracking-wider">{ref.code}</span>
      </p>
    </div>
  );
}

export default function CheckoutSuccess() {
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const kind = sp.get("kind") || "order";
  const { refresh, user } = useAuth();
  const [state, setState] = useState({ status: "polling", attempts: 0 });

  useEffect(() => {
    if (!sid) return;
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts >= 8) {
        setState({ status: "timeout", attempts });
        return;
      }
      attempts++;
      try {
        const url = kind === "membership" || kind === "founding1"
          ? `/memberships/status/${sid}`
          : kind === "drop"
            ? `/payments/status/${sid}`
            : `/orders/status/${sid}`;
        const { data } = await api.get(url);
        if (data.payment_status === "paid") {
          setState({ status: "paid", attempts, data });
          if (kind === "membership" || kind === "founding1") await refresh();
          if (kind === "drop") await refresh(); // pull updated free_shipping_credits
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", attempts });
          return;
        }
      } catch (e) { /* ignore */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sid, kind, refresh]);

  // Founding-member checkouts → redirect to celebratory /welcome once
  // webhook has stamped is_founding_member on the user.
  if (
    state.status === "paid" &&
    (kind === "membership" || kind === "founding1") &&
    user?.is_founding_member
  ) {
    return <Navigate to="/welcome" replace />;
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-success-page">
      {state.status === "polling" && (
        <>
          <div className="animate-pulse"><div className="w-20 h-20 rounded-full bg-[var(--ff-muted)] mx-auto" /></div>
          <h1 className="font-display text-4xl mt-8">Confirming your payment…</h1>
          <p className="mt-3 text-[var(--ff-text-secondary)]">This should only take a moment.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          {kind === "founding1" && (
            <div
              className="mb-8 rounded-2xl border-2 border-[var(--ff-citrus)] bg-gradient-to-br from-[#FFF8EE] to-[#FAF7F1] px-6 py-5 flex items-center gap-3 text-left mx-auto max-w-md shadow-[0_20px_50px_-30px_rgba(244,162,97,0.55)]"
              data-testid="founding1-seat-reserved-toast"
              role="status"
              aria-live="polite"
            >
              <span
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--ff-primary)]"
                style={{ background: "rgba(244,162,97,0.25)" }}
                aria-hidden
              >
                <CheckCircle2 size={20} />
              </span>
              <div>
                <p className="font-display text-lg leading-tight text-[var(--ff-text-primary)]">
                  Your founding seat is reserved.
                </p>
                <p className="text-xs text-[var(--ff-text-secondary)] mt-0.5">
                  $5 today · full annual charge begins on our launch day.
                </p>
              </div>
            </div>
          )}
          <CheckCircle2 size={72} className="mx-auto text-[var(--ff-primary)]" />
          <h1 className="font-display text-5xl mt-6" data-testid="success-headline">
            {kind === "founding1"
              ? "Welcome, Founding Member."
              : kind === "membership"
                ? "Welcome aboard!"
                : kind === "drop"
                  ? "Your drop is locked in!"
                  : "Order confirmed!"}
          </h1>
          <p className="mt-4 text-[var(--ff-text-secondary)] leading-relaxed">
            {kind === "founding1"
              ? "You've locked in one of the 200 founding seats. We'll email you the moment drops open — and your annual Preferred membership begins automatically on launch day."
              : kind === "membership"
                ? "Your membership is now active. The full marketplace is open to you."
                : kind === "drop"
                  ? "We've reserved your spot in the drop and notified the farm. You'll get a text the moment it ships."
                  : "We've sent your order to the farms. You'll receive delivery details by email."}
          </p>
          <div className="mt-10 flex gap-4 justify-center">
            <Link
              to={kind === "membership" || kind === "founding1"
                ? "/account"
                : kind === "drop" ? "/drops" : "/account"}
              className="btn-primary"
              data-testid="success-cta"
            >
              {kind === "founding1"
                ? "Go to my account"
                : kind === "membership"
                  ? "Start shopping"
                  : kind === "drop"
                    ? "See upcoming drops"
                    : "View orders"}
            </Link>
          </div>

          {(kind === "membership" || kind === "founding1") && <ReferralShareMoment />}
        </>
      )}
      {state.status === "paid" && <ReferralCelebrationModal kind={kind}/>}
      {(state.status === "expired" || state.status === "timeout") && (
        <>
          <h1 className="font-display text-4xl mt-6">Hmm — we couldn&apos;t confirm that.</h1>
          <p className="mt-3 text-[var(--ff-text-secondary)]">Please contact support if you were charged.</p>
          <Link to="/" className="btn-secondary mt-8 inline-flex">Back home</Link>
        </>
      )}
    </div>
  );
}
